import { Fragment, type ReactNode } from "react";
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqDetail, VendorRfqLine } from "../../types";
import { formatMonthYear } from "../../utils/deadline";
import { describeDelta, formatPct, formatSignedMoney, type LineQuote, type RateMap, type ResponseSummary } from "../../utils/response";
import LineDescription from "./LineDescription";
import RateInput from "./RateInput";

interface RfqLineTableProps {
  detail: VendorRfqDetail;
  rates: RateMap;
  summary: ResponseSummary;
  onRateChange: (lineId: number, value: string) => void;
  readOnly?: boolean;
  onAskLine?: (line: VendorRfqLine) => void;
  lineExtras?: (line: VendorRfqLine) => ReactNode;
}

export const askLabel = (line: VendorRfqLine): string => `Query ${line.room} · ${line.trade} · ${line.line_id}`;

export const quantityLabel = (line: VendorRfqLine): string => `${line.quantity.toLocaleString("en-IN")} ${line.uom}`;

export const itemLabel = (line: VendorRfqLine): string => [line.item_type === "MRC" ? "MRC" : "Non-MRC", line.item_code].filter(Boolean).join(" · ");

export const rateLabel = (rate: number, unit: string): string => `${formatMoney(rate)} / ${unit}`;

export const bidFormula = (quote: LineQuote, line: VendorRfqLine): string => `₹${quote.rate} × ${quantityLabel(line)}`;

export const rateInputLabel = (line: VendorRfqLine): string => `Your rate for ${line.room} · ${line.trade} · ${line.line_id}`;

export const lastAcceptedLabel = (line: VendorRfqLine): string | null =>
  line.last_accepted_rate ? `Your last accepted rate here: ${rateLabel(line.last_accepted_rate.rate, line.uom)} · ${formatMonthYear(line.last_accepted_rate.month)}` : null;

export const deltaColor = (delta: number): string => (delta < 0 ? V2_COLORS.success : delta > 0 ? V2_COLORS.error : V2_COLORS.textSecondary);

const caption = { fontSize: "11px", color: V2_COLORS.textSecondary } as const;

export default function RfqLineTable({ detail, rates, summary, onRateChange, readOnly = false, onAskLine, lineExtras }: RfqLineTableProps) {
  const quotes = new Map(summary.lines.map((quote) => [quote.line_id, quote]));
  const columnCount = onAskLine ? 7 : 6;

  return (
    <Box sx={{ backgroundColor: "white", border: `1px solid ${V2_COLORS.border}`, borderRadius: "8px", overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Room / line item</TableCell>
            <TableCell>Description & attachments</TableCell>
            <TableCell>Qty · ask rate</TableCell>
            <TableCell align="right">Ask price</TableCell>
            <TableCell align="right">Your rate → bid</TableCell>
            <TableCell align="right">Delta vs ask</TableCell>
            {onAskLine && <TableCell align="right">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.lines.map((line) => {
            const quote = quotes.get(line.line_id) as LineQuote;
            const changed = !readOnly && quote.changed;
            const lastAccepted = lastAcceptedLabel(line);
            const extras = lineExtras?.(line);
            return (
              <Fragment key={line.line_id}>
              <TableRow data-testid="line-row" data-changed={String(changed)} sx={{ backgroundColor: changed ? V2_COLORS.primaryBg : undefined, verticalAlign: "top" }}>
                <TableCell>
                  <Typography fontSize="11px" fontWeight={600} sx={{ color: V2_COLORS.primary, letterSpacing: "0.5px" }}>
                    {line.room}
                  </Typography>
                  <Typography fontSize="14px" fontWeight={600}>
                    {line.trade}
                  </Typography>
                  <Typography fontSize="12px">{line.item_name}</Typography>
                  <Typography sx={caption}>{itemLabel(line)}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <LineDescription line={line} />
                </TableCell>
                <TableCell>
                  <Typography fontSize="13px">{quantityLabel(line)}</Typography>
                  <Typography sx={caption}>{rateLabel(line.ask_rate, line.uom)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontSize="14px" fontWeight={600}>
                    {formatMoney(line.ask_amount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {readOnly ? (
                    <Typography fontSize="14px" fontWeight={600}>
                      {quote.rate === null ? "—" : rateLabel(quote.rate, line.uom)}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <RateInput label={rateInputLabel(line)} value={rates[line.line_id] ?? ""} unit={line.uom} onChange={(value) => onRateChange(line.line_id, value)} error={quote.error} />
                      <Typography sx={{ ...caption, mt: 0.5 }}>{`per ${line.uom}`}</Typography>
                    </Box>
                  )}
                  {quote.rate !== null && (
                    <>
                      <Typography fontSize="13px" fontWeight={600} sx={{ mt: 0.5 }}>
                        {formatMoney(quote.amount)}
                      </Typography>
                      <Typography sx={caption}>{bidFormula(quote, line)}</Typography>
                    </>
                  )}
                  {lastAccepted && (
                    <Typography fontSize="11px" sx={{ color: V2_COLORS.primary, mt: 0.5 }}>
                      {lastAccepted}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {quote.rate === null ? (
                    <Typography sx={caption}>—</Typography>
                  ) : (
                    <>
                      <Typography fontSize="14px" fontWeight={600} sx={{ color: deltaColor(quote.delta) }}>
                        {formatSignedMoney(quote.delta)}
                      </Typography>
                      <Typography sx={caption}>{describeDelta(quote.delta)}</Typography>
                    </>
                  )}
                </TableCell>
                {onAskLine && (
                  <TableCell align="right">
                    <Button variant="outlined" size="small" aria-label={askLabel(line)} onClick={() => onAskLine(line)} sx={{ textTransform: "none" }}>
                      Query
                    </Button>
                  </TableCell>
                )}
              </TableRow>
              {extras && (
                <TableRow data-testid="line-extras">
                  <TableCell colSpan={columnCount} sx={{ backgroundColor: V2_COLORS.bgMuted }}>
                    {extras}
                  </TableCell>
                </TableRow>
              )}
              </Fragment>
            );
          })}
          <TableRow data-testid="totals-row" sx={{ backgroundColor: V2_COLORS.bgMuted }}>
            <TableCell colSpan={3}>
              <Typography fontSize="13px" fontWeight={600}>
                Totals
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography sx={caption}>Ask total</Typography>
              <Typography fontSize="14px" fontWeight={600}>
                {formatMoney(summary.askTotal)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography sx={caption}>Your bid total</Typography>
              <Typography fontSize="14px" fontWeight={700} sx={{ color: summary.changed ? V2_COLORS.success : undefined }}>
                {summary.valid ? formatMoney(summary.total) : "—"}
              </Typography>
            </TableCell>
            <TableCell align="right">
              {summary.valid ? (
                <>
                  <Typography fontSize="14px" fontWeight={600} sx={{ color: deltaColor(summary.delta) }}>
                    {`${formatSignedMoney(summary.delta)} (${formatPct(summary.deltaPct)})`}
                  </Typography>
                  <Typography sx={caption}>{describeDelta(summary.delta)}</Typography>
                </>
              ) : (
                <Typography sx={caption}>—</Typography>
              )}
            </TableCell>
            {onAskLine && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
